import React, { ReactElement, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material'
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
} from '@mui/icons-material'
import JsonView from '@uiw/react-json-view'
import dayjs from 'dayjs'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchMotherNodeDetail,
  selectMotherNodeDetail,
  selectMotherNodeDetailStatus,
} from '../mother.slice'

type NodeDetailModalProps = {
  open: boolean
  onClose: () => void
  correlationID: string
  nodeID: string
}

let parseJsonSafe = (jsonString: string | null): any => {
  if (!jsonString) return null
  try {
    return JSON.parse(jsonString)
  } catch {
    return jsonString // Return as-is if not valid JSON
  }
}

let extractStatusValue = (status: string): string => {
  let parsed = parseJsonSafe(status)

  if (typeof parsed === 'string') {
    return parsed
  }

  if (typeof parsed === 'object' && parsed !== null) {
    // Check if it's a discriminated union (Rust enum style) - the key is the status
    let keys = Object.keys(parsed)
    if (keys.length === 1) {
      return keys[0] // Return the key as the status (e.g., "NodeCompleted", "NodeFailed")
    }

    // Try common status field names
    return (
      parsed.status ||
      parsed.state ||
      parsed.nodeStatus ||
      parsed.Status ||
      parsed.State ||
      JSON.stringify(parsed)
    )
  }

  return status
}

let getStatusColor = (
  status: string,
): 'success' | 'warning' | 'error' | 'default' => {
  let statusValue = extractStatusValue(status)

  if (statusValue?.toLowerCase().includes('complete')) return 'success'
  if (statusValue?.toLowerCase().includes('fail')) return 'error'
  if (
    statusValue?.toLowerCase().includes('progress') ||
    statusValue?.toLowerCase().includes('pending')
  )
    return 'warning'
  return 'default'
}

let getStatusIcon = (status: string) => {
  let color = getStatusColor(status)
  if (color === 'success')
    return <CheckCircleIcon color="success" fontSize="small" />
  if (color === 'error') return <ErrorIcon color="error" fontSize="small" />
  if (color === 'warning')
    return <PendingIcon color="warning" fontSize="small" />
  return null
}

export default ({
  open,
  onClose,
  correlationID,
  nodeID,
}: NodeDetailModalProps): ReactElement => {
  let dispatch = useAppDispatch()
  let nodeDetail = useAppSelector(selectMotherNodeDetail(correlationID, nodeID))
  let nodeDetailStatus = useAppSelector(
    selectMotherNodeDetailStatus(correlationID, nodeID),
  )

  useEffect(() => {
    if (open && nodeDetailStatus === 'uninitialized') {
      dispatch(fetchMotherNodeDetail({ correlationID, nodeID }))
    }
  }, [open, dispatch, correlationID, nodeID, nodeDetailStatus])

  let parsedStep = parseJsonSafe(nodeDetail?.step || null)
  let parsedStatus = parseJsonSafe(nodeDetail?.status || null)
  let parsedLore = parseJsonSafe(nodeDetail?.lore || null)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            bgcolor: 'background.default',
            borderRadius: 2,
            maxHeight: '90vh',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {nodeDetail && getStatusIcon(nodeDetail.status)}
          <Typography variant="h6" component="span">
            Node Details
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {nodeDetailStatus === 'pending' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {nodeDetailStatus === 'rejected' && (
          <Alert severity="error">Failed to load node details</Alert>
        )}

        {nodeDetailStatus === 'fulfilled' && nodeDetail && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Node Info */}
            <Paper sx={{ p: 2, bgcolor: 'background.paperDark' }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Node ID
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: 'monospace', mb: 2 }}
              >
                {nodeDetail.nodeID}
              </Typography>

              <Box
                sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}
              >
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body2">
                    {dayjs(nodeDetail.createdAt).format('MMM D, h:mm:ss A')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Updated
                  </Typography>
                  <Typography variant="body2">
                    {dayjs(nodeDetail.updatedAt).format('MMM D, h:mm:ss A')}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Status */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Status
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'background.paperDark' }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <Chip
                    label={extractStatusValue(nodeDetail.status)}
                    color={getStatusColor(nodeDetail.status)}
                    size="small"
                  />
                </Box>
                {typeof parsedStatus === 'object' && parsedStatus !== null && (
                  <JsonView
                    value={parsedStatus}
                    style={
                      {
                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                        fontSize: '13px',
                        '--w-rjv-color': '#ffffff',
                        '--w-rjv-key-string': '#9db7ff',
                        '--w-rjv-background-color': 'transparent',
                      } as any
                    }
                    displayDataTypes={false}
                    displayObjectSize={false}
                    enableClipboard={true}
                    collapsed={1}
                  />
                )}
              </Paper>
            </Box>

            {/* Step Details */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Step Configuration
              </Typography>
              <Paper
                sx={{
                  p: 2,
                  bgcolor: 'background.paperDark',
                  maxHeight: 300,
                  overflowY: 'auto',
                }}
              >
                <JsonView
                  value={parsedStep}
                  style={
                    {
                      backgroundColor: 'rgba(0, 0, 0, 0.1)',
                      fontSize: '13px',
                      '--w-rjv-color': '#ffffff',
                      '--w-rjv-key-string': '#9db7ff',
                      '--w-rjv-background-color': 'transparent',
                    } as any
                  }
                  displayDataTypes={false}
                  displayObjectSize={false}
                  enableClipboard={true}
                  collapsed={1}
                />
              </Paper>
            </Box>

            {/* Dependencies & Children */}
            {(nodeDetail.dependencies.length > 0 ||
              nodeDetail.children.length > 0) && (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Relationships
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'background.paperDark' }}>
                  {nodeDetail.dependencies.length > 0 && (
                    <Box sx={{ mb: nodeDetail.children.length > 0 ? 2 : 0 }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Dependencies ({nodeDetail.dependencies.length})
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {nodeDetail.dependencies.map((dep) => (
                          <Chip
                            key={dep}
                            label={dep.slice(0, 8)}
                            size="small"
                            variant="outlined"
                            sx={{ fontFamily: 'monospace' }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {nodeDetail.children.length > 0 && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Children ({nodeDetail.children.length})
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {nodeDetail.children.map((child) => (
                          <Chip
                            key={child}
                            label={child.slice(0, 8)}
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ fontFamily: 'monospace' }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Paper>
              </Box>
            )}

            {/* Lore Output */}
            {nodeDetail.lore && (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Reactor Output (Lore)
                </Typography>
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: 'background.paperDark',
                    maxHeight: 300,
                    overflowY: 'auto',
                  }}
                >
                  <JsonView
                    value={parsedLore}
                    style={
                      {
                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                        fontSize: '13px',
                        '--w-rjv-color': '#ffffff',
                        '--w-rjv-key-string': '#9db7ff',
                        '--w-rjv-background-color': 'transparent',
                      } as any
                    }
                    displayDataTypes={false}
                    displayObjectSize={false}
                    enableClipboard={true}
                    collapsed={1}
                  />
                </Paper>
              </Box>
            )}

            {/* Error Message */}
            {nodeDetail.errorMessage && (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Error Message
                </Typography>
                <Alert
                  severity="error"
                  sx={{ fontFamily: 'monospace', fontSize: '13px' }}
                >
                  {nodeDetail.errorMessage}
                </Alert>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}
